import { useCallback, useEffect, useMemo } from 'react';
import { FormConfig, FormField } from 'components/common/form/FormConfig';
import { useOutdoorDurationFields } from './useOutdoorDurationFields';
import { useFormikContext } from 'formik';
import { floor, get, isNil } from 'lodash';
import { OutdoorType } from 'core/creative/Creative';
import i18n from 'i18n';
import { useOutdoorProperties } from './useOutdoorProperties';
import { VideoOutdoorCreativeFields } from 'core/creative/OutdoorCreative';
import { OutdoorFormModel } from '../OutdoorFormModel';
import defaultOutdoorVideo from 'assets/creative/default_outdoor_video.mp4';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons';
import styles from './outdoorFields.module.scss';
import { ChannelGroupSetting } from 'core/channelGroup/ChannelGroupSettings';
import { ChannelType } from 'core/channelGroup/ChannelGroup';

export const useVideoOutdoorFields = <T extends OutdoorFormModel<T>>(
  model: T,
  onSizeInstructionBtnClick: () => void
): FormField[] => {
  const { values, setFieldValue } =
    useFormikContext<VideoOutdoorCreativeFields>();
  const noCreativeSizeLimit = isNil(model.creativeSizes);
  const isEditing = !!values.creativeId;
  const { outdoorType, width, height } = useOutdoorProperties(values);

  const durationFields = useOutdoorDurationFields(
    isEditing || model.useMediaDuration ? 'label' : 'select'
  );

  const allowSizeBoxing = Object.keys(model.sizeBoxingMap).length > 0;

  const onMediaFileChange = useCallback(
    (fileData) => {
      if (fileData) {
        // POS-7999：H.265 在瀏覽器無法解碼時 metadata 為空，不可寫入 NaN 的 duration/尺寸
        // （會序列化成 null 導致後端解析失敗）；duration 後端會以實際分析結果重算。
        if (fileData.duration !== undefined) {
          setFieldValue('typeProperties.duration', floor(fileData.duration));
        }
        noCreativeSizeLimit &&
          fileData.width !== undefined &&
          fileData.height !== undefined &&
          setFieldValue(
            'typeProperties.creativeSize',
            `${fileData.width} x ${fileData.height}`
          );
      }
    },
    [noCreativeSizeLimit, setFieldValue]
  );

  const initUploadVideo = useCallback(() => {
    const videoField = get(values, 'medias.video');
    if (!defaultOutdoorVideo || videoField || isEditing) {
      return;
    }

    // Fetch video metadata
    fetch(defaultOutdoorVideo)
      .then((res) => res.blob())
      .then((blob) => {
        const file = new File([blob], 'default_outdoor_video.mp4', {
          type: 'video/mp4'
        });

        // Load video to get metadata
        const video = document.createElement('video');
        const url = URL.createObjectURL(file);

        video.preload = 'metadata';
        video.src = url;
        video.addEventListener(
          'loadedmetadata',
          () => {
            const duration = Math.max(1, floor(video.duration));
            onMediaFileChange({
              width: video.videoWidth,
              height: video.videoHeight,
              duration
            });

            // Trigger setFieldValue with loaded metadata as if it was uploaded by user
            setFieldValue('medias.video.file', file);
            setFieldValue('medias.video.width', video.videoWidth);
            setFieldValue('medias.video.height', video.videoHeight);
            setFieldValue('medias.video.duration', duration);

            // Clean up object URL after usage
            URL.revokeObjectURL(url);
          },
          { once: true }
        );
      });
  }, [isEditing, onMediaFileChange, setFieldValue, values]);

  useEffect(() => {
    const creativeTypeSetting = ChannelGroupSetting.creativeTypeSettings().get(
      values.creativeType
    );
    const channelGroupSetting = creativeTypeSetting
      ? ChannelGroupSetting.fromId(creativeTypeSetting.channelGroupId)
      : undefined;
    if (channelGroupSetting?.config.channelType === ChannelType.BEACON) {
      initUploadVideo();
    }
  }, [initUploadVideo, values.creativeType]);

  const fields = useMemo(() => {
    if (outdoorType !== OutdoorType.VIDEO) {
      return [];
    }

    const builder = new FormConfig.FieldsBuilder();
    builder.addFormikFileInput({
      type: 'video',
      label: i18n.t('videoForm.labels.tenMaxVideoInput'),
      name: 'medias.video',
      maxWidth: noCreativeSizeLimit ? 500 : Math.min(width / 3, 500),
      maxHeight: noCreativeSizeLimit ? 500 : Math.min(height / 3, 500),
      hints: model.getVideoHints(width, height),
      permanentHint: allowSizeBoxing ? (
        <div
          className={styles.sizeInstructionBtn}
          onClick={onSizeInstructionBtnClick}
        >
          {i18n.t('outdoorCreativeSizeHint.labels.creativeInfoHint')}
          <FontAwesomeIcon icon={faInfoCircle} />
        </div>
      ) : undefined,
      validate: (value) => model.validateVideo(width, height, value),
      onChange: model.useMediaDuration ? onMediaFileChange : undefined,
      // POS-7999：H.265 影片在瀏覽器無法解碼時，保留檔案、metadata 交後端分析回填
      deferMetadataToServer: true
    });

    return [...builder.build(), ...durationFields];
  }, [
    model,
    outdoorType,
    width,
    height,
    durationFields,
    noCreativeSizeLimit,
    allowSizeBoxing,
    onMediaFileChange,
    onSizeInstructionBtnClick
  ]);

  return fields;
};
